import { Button } from 'primereact/button';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ContractForm from '../../../features/accounting/forms/NewContract/ContractForm';
import JobForm from '../../../features/accounting/forms/NewContract/JobForm';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCompanyContext } from '../../../context/CompanyContext';
import ContractFormSteps, {
  checkBeforeStep,
} from '../../../features/accounting/forms/NewContract/ContractFormSteps';
import GeneralInfoForm from '../../../features/accounting/forms/NewContract/GeneralInfoForm';
import {
  ContractDraftAttachment,
  NewContractFields,
} from '../../../Interfaces/Accounting/ACForms.interfaces';
import { useMutation, useQuery } from '@tanstack/react-query';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import {
  fetchContractDraft,
  postContractDraft,
  putContractDraft,
  putSubmitContractDraft,
} from '../../../services/accounting/ACFormsService';
import ContractAttachments from '../../../features/accounting/forms/NewContract/ContractAttachments';
import ContractSuccessMessage from '../../../features/accounting/forms/NewContract/ContractSuccessMessage';
import LoadingButton from '../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import { ApiError } from '../../../Interfaces/system/Generic.interfaces';
import ContractCopyInfo from '../../../features/accounting/forms/NewContract/ContractCopyInfo';
import {
  buildContractDraftPayload,
  buildEmptyContractForm,
  reviveContractDraftPayload,
} from '../../../features/accounting/forms/NewContract/utils';
import { ProgressSpinner } from 'primereact/progressspinner';

const NewContract = () => {
  const { selectedCompany } = useCompanyContext();
  const navigate = useNavigate();
  const formMethods = useForm<NewContractFields>({ mode: 'onBlur' });
  const { handleSubmit, trigger, formState, getValues } = formMethods;
  const [activeIndex, setActiveIndex] = useState(0);
  const [draftID, setDraftID] = useState<string>();
  const [draftAttachments, setDraftAttachments] = useState<
    ContractDraftAttachment[]
  >([]);
  const [removeAttachments, setRemoveAttachments] = useState<string[]>([]);
  const [formKey, setFormKey] = useState(0);
  const [cleared, setCleared] = useState(false);
  const toast = useRef<Toast>(null);

  const hasChanges = formState.isDirty || !!removeAttachments.length || cleared;

  const draftRequest = useQuery({
    queryKey: ['getContractDraft', selectedCompany],
    queryFn: ({ signal }) => fetchContractDraft(selectedCompany!.id, signal),
    onSuccess: (draft) => {
      setDraftID(draft?.id);
      setDraftAttachments(draft?.draftAttachments ?? []);
      setRemoveAttachments([]);
      setCleared(false);
      setActiveIndex(0);
      formMethods.reset(
        draft
          ? reviveContractDraftPayload(draft.payload)
          : buildEmptyContractForm(formMethods.getValues())
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
    cacheTime: 0,
  });

  const saveRequest = useMutation({
    mutationFn: (data: NewContractFields) => {
      const payload = buildContractDraftPayload(data);

      if (draftID) {
        return putContractDraft(
          draftID,
          selectedCompany!.id,
          payload,
          removeAttachments
        );
      }

      return postContractDraft(selectedCompany!.id, payload);
    },
    onSuccess: ({ draft, error }) => {
      setDraftID(draft.id);
      setDraftAttachments(draft.draftAttachments ?? []);
      setRemoveAttachments([]);
      setCleared(false);
      formMethods.reset({ ...getValues(), attachments: undefined });

      if (error?.length) {
        showToast(
          'warn',
          toast,
          'Save Contract Draft',
          `These files couldn't be uploaded: ${error.join(', ')}`,
          10000
        );
        return;
      }

      showToast(
        'success',
        toast,
        'Save Contract Draft',
        'The draft was saved successfully',
        3000
      );
    },
    onError: (error: ApiError) => {
      showToast(
        'error',
        toast,
        'Save Contract Draft Error',
        error.response?.data?.message ||
          'An error ocurred! please try again later',
        10000
      );
    },
  });

  const submitRequest = useMutation({
    mutationFn: () => putSubmitContractDraft(draftID ?? ''),
    onError: (error: ApiError) => {
      showToast(
        'error',
        toast,
        'Create Contract Error',
        error.response?.data?.message ||
          'An error ocurred! please try again later',
        10000
      );
    },
  });

  const { reset: resetSaveRequest } = saveRequest;
  const { reset: resetSubmitRequest } = submitRequest;

  const changeIndex = (number: number) => {
    setActiveIndex(number);
  };

  const checkForm = async (index: number) => {
    const shouldChange = await checkBeforeStep(index, trigger);

    if (shouldChange) {
      setActiveIndex(index + 1);
    }
  };

  const clearForm = () => {
    formMethods.reset(buildEmptyContractForm(formMethods.getValues()));
    setRemoveAttachments((names) => [
      ...names,
      ...draftAttachments.map((attachment) => attachment.uniqueName),
    ]);
    setDraftAttachments([]);
    setFormKey((key) => key + 1);
    setCleared(true);
    setActiveIndex(0);
  };
  const clearFormCB = useCallback(clearForm, [
    formMethods,
    draftAttachments,
    setActiveIndex,
  ]);

  const removeDraftAttachment = (uniqueName: string) => {
    setDraftAttachments((attachments) =>
      attachments.filter((attachment) => attachment.uniqueName !== uniqueName)
    );
    setRemoveAttachments((names) => [...names, uniqueName]);
  };

  useEffect(() => {
    resetSaveRequest();
    resetSubmitRequest();
  }, [selectedCompany, resetSaveRequest, resetSubmitRequest, clearFormCB]);

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <FormProvider {...formMethods}>
        <div className="text-35px font-bold text-center mb-2 relative">
          New Contract Setup
          {!submitRequest.isSuccess && (
            <div className="flex justify-content-center lg:absolute top-0 left-0 mt-4 lg:mt-0 lg:ml-6">
              <ContractCopyInfo
                success={() => {
                  changeIndex(0);
                }}
              />
            </div>
          )}
        </div>
        {draftRequest.isFetching && (
          <div className="text-center mx-auto mt-3">
            <ProgressSpinner />
          </div>
        )}
        {!draftRequest.isFetching && !submitRequest.isSuccess && (
          <React.Fragment>
            <div className="w-screen max-w-900px mx-auto mb-2">
              <ContractFormSteps
                index={activeIndex}
                changeIndex={changeIndex}
              />
            </div>
            <form
              key={formKey}
              className="max-w-600px mx-auto flex flex-column gap-5"
            >
              <div className={`${activeIndex !== 0 && 'hidden'}`}>
                <ContractForm />
              </div>
              <div className={`${activeIndex !== 1 && 'hidden'}`}>
                <JobForm />
              </div>
              <div className={`${activeIndex !== 2 && 'hidden'}`}>
                <GeneralInfoForm />
              </div>
              <div className={`${activeIndex !== 3 && 'hidden'}`}>
                <ContractAttachments
                  draftID={draftID}
                  draftAttachments={draftAttachments}
                  removeDraftAttachment={removeDraftAttachment}
                />
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <ConfirmationDialog
                  tagKey="contract-clear"
                  Button={
                    <LoadingButton
                      isLoading={false}
                      label="Clear Form"
                      className="w-15rem bg-orange-500 border-0 flex m-auto py-3"
                      fontSize="text-22px"
                      type="button"
                    />
                  }
                  onConfirm={clearForm}
                  message="Are you sure you want to clear the form data?"
                />
                <ConfirmationDialog
                  tagKey="contract-save"
                  Button={
                    <LoadingButton
                      isLoading={saveRequest.isPending}
                      label="Save"
                      className="w-15rem buttonSecondary border-0 flex m-auto py-3"
                      fontSize="text-22px"
                      type="button"
                      disabled={!hasChanges || draftRequest.isFetching}
                    />
                  }
                  onConfirm={() => saveRequest.mutate(getValues())}
                  message="Are you sure you want to save this contract draft?"
                />
                <Button
                  onClick={() => {
                    setActiveIndex((index) => index - 1);
                  }}
                  className="w-15rem buttonSecondary border-0 flex m-auto py-3"
                  type="button"
                  disabled={activeIndex === 0}
                >
                  <span className="mx-auto text-22px text-white">Previous</span>
                </Button>
                {activeIndex !== 3 && (
                  <Button
                    className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                    type="button"
                    onClick={() => checkForm(activeIndex)}
                  >
                    <span className="mx-auto text-22px text-white">Next</span>
                  </Button>
                )}
                {activeIndex === 3 && (
                  <ConfirmationDialog
                    tagKey="contract-submit"
                    Button={
                      <LoadingButton
                        isLoading={submitRequest.isPending}
                        label="Create Contract"
                        className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                        fontSize="text-22px"
                        type="button"
                        disabled={!draftID || hasChanges}
                      />
                    }
                    onConfirm={handleSubmit(() => submitRequest.mutate())}
                    message="Are you sure you want to submit this contract?"
                  />
                )}
                <Button
                  onClick={() => {
                    navigate('/accounting-dashboard/new');
                  }}
                  className="w-15rem bluwaiRed border-0 flex m-auto py-3"
                  type="button"
                >
                  <span className="mx-auto text-22px text-white">Cancel</span>
                </Button>
              </div>
            </form>
          </React.Fragment>
        )}
        {submitRequest.isSuccess && (
          <ContractSuccessMessage
            extraJobNumber={submitRequest.data?.extraJobNumber}
            attachments={draftAttachments}
          />
        )}
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default NewContract;
